import { useAtomValue } from 'jotai';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { EventCard, EventCardSkeleton } from '../EventCard';
import { GenericErrorBoundary } from '@/components/common/GenericErrorBoundary';
import {
  eventsObservableAtomFamily,
  filteredEventsObservableAtomFamily,
  selectedClientForFilteringAtom,
} from '@/state/events/atoms';
import { propertyAddressMapAtom } from '@/features/property-search/atoms';
import type { CaseStatus } from '@/types/api/events';

interface EventColumnProps {
  title: string;
  eventType: CaseStatus;
  emptyMessage?: string;
  limit?: number;
  showMoreIndicator?: boolean;
  isTogglable?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
}

export function EventColumn({
  title,
  eventType,
  emptyMessage = 'No events found',
  limit,
  showMoreIndicator = false,
  isTogglable = false,
  isExpanded = false,
  onToggle,
}: EventColumnProps) {
  const selectedClientId = useAtomValue(selectedClientForFilteringAtom);

  // Use filtered atom if client is selected, otherwise use regular atom
  const eventsResponse = useAtomValue(
    selectedClientId
      ? filteredEventsObservableAtomFamily({
          eventType,
          clientId: selectedClientId,
        })
      : eventsObservableAtomFamily(eventType),
  );

  const propertyAddressMap = useAtomValue(propertyAddressMapAtom);
  const events = eventsResponse?.events || [];

  // Apply limit if specified
  const displayEvents = limit ? events.slice(0, limit) : events;
  const hasMoreEvents = limit && events.length > limit;

  return (
    <div className="h-full flex flex-col rounded-lg overflow-hidden">
      {/* Column Header */}
      <div
        className={`flex items-center justify-between p-2 ps-4 ${
          isTogglable ? 'cursor-pointer transition-all' : ''
        }`}
        onClick={isTogglable ? onToggle : undefined}
      >
        <div className="flex items-center space-x-2">
          {isTogglable &&
            (isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-600" />
            ))}
          <h2 className="font-semibold text-gray-900">{title}</h2>
        </div>
        {showMoreIndicator && hasMoreEvents && (
          <span className="text-sm text-gray-500">
            +{events.length - limit!} more
          </span>
        )}
      </div>

      {/* Events List */}
      <div className="flex-1 overflow-y-auto">
        {displayEvents.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-sm">{emptyMessage}</div>
          </div>
        ) : (
          <>
            {displayEvents.map((event) => (
              <GenericErrorBoundary
                key={event.event_id}
                fallback={<EventCardSkeleton variant="error" />}
              >
                <EventCard
                  event={event}
                  clientName={propertyAddressMap[event.client_id]}
                />
              </GenericErrorBoundary>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
